import {
  memo,
  useCallback,
  useId,
  useMemo,
  useState,
  type ElementType,
} from 'react';
import {
  Briefcase,
  CalendarClock,
  CalendarRange,
  UserPlus,
  Users,
  type LucideProps,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  YAxis,
  type TooltipProps,
} from 'recharts';
import { CompanyLeaveStatus } from '@gokrew/models/leave-management';

import BGCard from '../../Cards/BGCard';
import { useGetDashboardCards } from '@/hooks/dashboard/useDashboard';
import { TranslationNS } from '@/constants/translation-namespaces';
import { usePermissions } from '@/context/PermissionContext';
import { permissionKeys } from '@/utility';
import type { PermissionScope } from '@/types/permissions';
import type { DailyPoint, MonthlyPoint } from '@/types/dashboard';
import { OnboardingFeatureKey } from '@gokrew/models/company';
import { useModuleEnabled } from '@/hooks/useModuleEnabled';
import UpcomingChangesDrawer from '../UpcomingChangesDrawer';
import styles from './Summary.module.css';

/** Shown instead of a number when the cards request hasn't resolved. */
const NO_VALUE = '—';

/* A curve needs two points to exist. Below that the card keeps an empty plot
   area so it still lines up with its neighbours. */
const MIN_SPARK_POINTS = 2;

/* Height a card of nothing but zeros is scaled against, so its line lands on
   the floor instead of dividing by a zero range. */
const EMPTY_SPARK_MAX = 1;

/* Only a top inset, so the stroke's upper half isn't clipped at a peak. */
const SPARK_MARGIN = { top: 2, right: 0, bottom: 0, left: 0 };

/* Marks the hovered point only — the curve is plotted bare, so a per-point dot
   would turn a 3.25rem strip into a row of beads. */
const SPARK_ACTIVE_DOT = {
  r: 3,
  fill: 'var(--stat-fg)',
  stroke: 'var(--card)',
  strokeWidth: 2,
};

/* Pinned to the top edge of the plot instead of tracking the cursor's y: the
   card clips its overflow, so a tooltip that floated above the curve would be
   cut off. x is left to Recharts, which keeps it inside the plot. */
const SPARK_TOOLTIP_POSITION = { y: 0 };

/* Recharts writes `cursor: default` inline on its wrapper, which outranks the
   module's rule and leaves the strip looking unclickable even though it
   navigates. Its own style prop is spread last, so this is what wins. */
const SPARK_STYLE = { cursor: 'pointer' };

interface SparkPoint {
  /** Period this point counts, as the hover tooltip names it. */
  label: string;
  value: number;
}

interface StatCardData {
  key: string;
  scope: PermissionScope;
  name: string;
  value: string | number;
  suffix?: string;
  caption: string;
  /** Let a long caption wrap instead of clipping. */
  wrapCaption?: boolean;
  icon: ElementType<LucideProps>;
  toneClass: string;
  path: string;
  /** Opens something in place instead of navigating to `path`. */
  onClick?: () => void;
  series: SparkPoint[];
}

/* Both period fields are company-local — `date` a YYYY-MM-DD and `period` a
   YYYY-MM — so each is parsed field by field rather than through Date's UTC
   string path, which would slide a point onto the neighbouring day. */
const parseLocalPeriod = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day ?? 1);
};

/* Daily and monthly series differ only in their period field, so both collapse
   to the same labelled list. The curve carries no axis, which makes the label
   the only thing naming what a hovered point counts. */
const seriesPoints = (
  points: (DailyPoint | MonthlyPoint)[] | undefined,
  locale: string
): SparkPoint[] => {
  if (!points?.length) return [];

  const format = new Intl.DateTimeFormat(
    locale,
    'date' in points[0]
      ? { weekday: 'short', day: 'numeric', month: 'short' }
      : { month: 'short', year: 'numeric' }
  );

  return points.map((point) => ({
    label: format.format(
      parseLocalPeriod('date' in point ? point.date : point.period)
    ),
    value: point.value,
  }));
};

function buildPendingLeaveQuery(pendingLabel: string) {
  const params = new URLSearchParams();
  params.append('status[][label]', pendingLabel);
  params.append('status[][value]', CompanyLeaveStatus.Pending);
  params.append('page', '1');
  params.append('limit', '5');
  params.append('employees', '');
  params.append('departments', '');
  return params.toString();
}

// ─── Sparkline ────────────────────────────────────────────────────────────────

/* Same shape as the TrendAreaChart tooltip — period over on the left, value on
   the right — trimmed to one row, since the card names the metric already and
   there is only ever one series under a card.

   `payload` and `active` are redeclared as optional rather than taken from
   Recharts' own content props, which make them required — Recharts fills both
   in at render time, so the JSX that mounts this can't supply them. Same
   workaround the shared TrendAreaChart tooltip uses. */
interface SparkTooltipProps extends TooltipProps<number, string> {
  payload?: { payload: SparkPoint }[];
  active?: boolean;
  locale: string;
}

const SparkTooltip = ({ active, payload, locale }: SparkTooltipProps) => {
  const point = payload?.[0]?.payload;

  if (!active || !point) return null;

  return (
    <div className={styles.sparkTooltip}>
      <span className={styles.sparkTooltipLabel}>{point.label}</span>
      <span className={styles.sparkTooltipValue}>
        {point.value.toLocaleString(locale)}
      </span>
    </div>
  );
};

const StatSpark = ({
  series,
  locale,
  onClick,
}: {
  series: SparkPoint[];
  locale: string;
  onClick: () => void;
}) => {
  /* Gradient ids must be unique per instance — four cards render at once and
     a shared id would make every card paint the first card's gradient. */
  const gradientId = `${useId()}-stat-spark`;

  /* Zero is the floor and the card's own peak is the ceiling: a value of none
     sits on the bottom edge and anything at the peak fills the card, so a flat
     series of eights reads as full rather than as a line hugging the floor.
     Scaled per card, since each counts a different thing and there is no
     cross-card comparison a shared scale would be protecting. */
  const domain = useMemo<[number, number]>(() => {
    const max = Math.max(...series.map((point) => point.value), 0);
    return [0, max || EMPTY_SPARK_MAX];
  }, [series]);

  if (series.length < MIN_SPARK_POINTS) {
    return <div className={styles.spark} aria-hidden="true" />;
  }

  /* Hidden from assistive tech and clickable rather than focusable: the curve
     is decoration over a number the button already reads out, and the click
     only repeats where that button goes. It has to sit above the button's
     stretched hit area, though, or the overlay swallows the hover the tooltip
     runs on — hence the forwarded click, which puts navigation back. */
  return (
    <div className={styles.spark} aria-hidden="true" onClick={onClick}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={series}
          margin={SPARK_MARGIN}
          accessibilityLayer={false}
          style={SPARK_STYLE}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" className={styles.sparkFrom} />
              <stop offset="100%" className={styles.sparkTo} />
            </linearGradient>
          </defs>
          <YAxis hide domain={domain} />
          <Tooltip
            content={<SparkTooltip locale={locale} />}
            cursor={false}
            position={SPARK_TOOLTIP_POSITION}
            offset={0}
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="value"
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={SPARK_ACTIVE_DOT}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// ─── Skeleton (shimmer idiom shared with AssetsOverview) ──────────────────────

/* Built from the real .statCard / .statBody classes so the placeholder sits on
   the same grid as the loaded cards and nothing shifts when data lands. */
const SummarySkeleton = ({ count }: { count: number }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <BGCard key={i} className={styles.statCard}>
        <div className={styles.statBody} aria-hidden="true">
          <div className={styles.header}>
            <span className={`${styles.skeletonBase} ${styles.skeletonName}`} />
            <span className={`${styles.skeletonBase} ${styles.skeletonIcon}`} />
          </div>
          <span className={`${styles.skeletonBase} ${styles.skeletonValue}`} />
          <span
            className={`${styles.skeletonBase} ${styles.skeletonCaption}`}
          />
        </div>
        <span className={`${styles.skeletonBase} ${styles.skeletonSpark}`} />
      </BGCard>
    ))}
  </>
);

// ─── Card ─────────────────────────────────────────────────────────────────────

const StatCard = ({
  name,
  value,
  suffix,
  caption,
  wrapCaption,
  icon: Icon,
  toneClass,
  path,
  onClick,
  series,
  locale,
}: Omit<StatCardData, 'key'> & { locale: string }) => {
  const navigate = useNavigate();
  const handleClick = useCallback(
    () => (onClick ? onClick() : navigate(path)),
    [navigate, onClick, path]
  );

  return (
    <BGCard className={`${styles.statCard} ${toneClass}`}>
      {/* The chart is a sibling, not a child, of the button: ResponsiveContainer
          renders a <div> and flow content isn't valid inside a <button>. The
          button's ::after stretches over the rest of the card, and the chart
          forwards its own clicks, so the whole card still navigates. */}
      <button type="button" className={styles.statBody} onClick={handleClick}>
        <span className={styles.header}>
          <span className={styles.name}>{name}</span>
          <span className={styles.iconWrap} aria-hidden="true">
            <Icon size={14} className={styles.icon} />
          </span>
        </span>

        <span className={styles.valueRow}>
          <span className={styles.value}>{value}</span>
          {suffix && <span className={styles.suffix}>{suffix}</span>}
        </span>

        <span
          className={`${styles.caption} ${wrapCaption ? styles.captionWrap : ''}`}
        >
          {caption}
        </span>
      </button>

      <StatSpark series={series} locale={locale} onClick={handleClick} />
    </BGCard>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const Summary = () => {
  const { t, i18n } = useTranslation(TranslationNS.Dashboard);
  const { t: tLeave } = useTranslation(TranslationNS.LeaveManagement);
  /* One request behind every card: each headline number and the curve under it
     come from the same payload, so no card plots a shape its own number can't
     account for. */
  const { data, isLoading } = useGetDashboardCards();
  const { permissions } = usePermissions();
  const isModuleEnabled = useModuleEnabled();
  const [upcomingOpen, setUpcomingOpen] = useState(false);
  const openUpcoming = useCallback(() => setUpcomingOpen(true), []);

  const cards = data?.data;
  const locale = i18n.language;
  const recruitingOn = isModuleEnabled(OnboardingFeatureKey.Recruiting);

  /* The KPI row (spec 2026-10-01, 1.1): Employees, Pending approvals,
     Upcoming changes, New joiners, Recruiting. Who is in or out today lives
     in Who's away, so those two cards are gone. */
  const stats = useMemo<StatCardData[]>(() => {
    const newJoiners = cards?.newJoiners;
    /* The headline counts the current month only, while the curve under it runs
       one point per month across the whole window — so the caption names that
       span rather than leaving the two looking like they measure the same thing. */
    const joinerMonths = newJoiners?.series.length ?? 0;
    const joinersInWindow =
      newJoiners?.series.reduce((sum, point) => sum + point.value, 0) ?? 0;

    const pending = cards?.pendingApprovals;
    const pendingTone = !pending
      ? styles.toneAmber
      : pending.overdue
        ? styles.toneRed
        : pending.total > 0
          ? styles.toneAmber
          : styles.toneGreen;
    const pendingCaption = !pending
      ? t('summary.updated_just_now')
      : pending.total === 0
        ? t('summary.all_caught_up')
        : pending.onMe > 0
          ? t('summary.on_me', { count: pending.onMe })
          : t('summary.none_on_me');

    const upcoming = cards?.upcomingChanges;
    const upcomingParts = upcoming
      ? [
          upcoming.starts && t('summary.starts', { count: upcoming.starts }),
          upcoming.departures &&
            t('summary.departures', { count: upcoming.departures }),
          upcoming.probationEnds &&
            t('summary.probation_ends', { count: upcoming.probationEnds }),
          upcoming.contractEnds &&
            t('summary.contract_ends', { count: upcoming.contractEnds }),
        ].filter(Boolean)
      : [];

    const recruiting = cards?.recruiting;

    const list: (StatCardData | null)[] = [
      {
        key: 'total_employees',
        scope: permissionKeys.employee.read,
        name: t('summary.total_employees'),
        value: cards?.myTeam.total ?? NO_VALUE,
        caption: t('summary.across_all_departments'),
        icon: Users,
        toneClass: styles.toneBlue,
        path: '/employees',
        series: seriesPoints(cards?.myTeam.series, locale),
      },
      {
        key: 'pending_approvals',
        scope: permissionKeys.leave_request.read,
        name: t('summary.pending_approvals'),
        value: pending?.total ?? NO_VALUE,
        suffix:
          pending && pending.total > 0 && pending.oldestDays != null
            ? t('summary.oldest_days', { count: pending.oldestDays })
            : undefined,
        caption: pendingCaption,
        icon: CalendarClock,
        toneClass: pendingTone,
        path: `/leave-management/overview?${buildPendingLeaveQuery(
          tLeave('leave_stat.pending')
        )}`,
        series: seriesPoints(pending?.series, locale),
      },
      {
        key: 'upcoming_changes',
        scope: permissionKeys.employee.read,
        name: t('summary.upcoming_changes'),
        value: upcoming?.total ?? NO_VALUE,
        suffix: t('summary.next_30_days'),
        caption: upcomingParts.length
          ? upcomingParts.join(' · ')
          : t('summary.nothing_upcoming'),
        wrapCaption: true,
        icon: CalendarRange,
        toneClass: styles.tonePurple,
        path: '',
        onClick: openUpcoming,
        // A future count has no history worth a curve (D62).
        series: [],
      },
      {
        key: 'new_joiners',
        scope: permissionKeys.employee.read,
        name: t('summary.new_joiners'),
        value: newJoiners?.total ?? NO_VALUE,
        suffix: t('summary.this_month'),
        /* Pluralised on the month span, not the joiner count — the span is
           the part of this line that can read as "1 months". */
        caption: joinersInWindow
          ? t('summary.joiners_in_window', {
              count: joinerMonths,
              joiners: joinersInWindow,
            })
          : t('summary.no_joiners_in_window', { count: joinerMonths }),
        icon: UserPlus,
        toneClass: styles.tonePurple,
        path: '/employees',
        series: seriesPoints(newJoiners?.series, locale),
      },
      recruitingOn && (recruiting || !cards)
        ? {
            key: 'recruiting',
            scope: permissionKeys.recruiting.read,
            name: t('summary.recruiting'),
            value: recruiting?.openJobs ?? NO_VALUE,
            suffix: t('summary.open_jobs'),
            caption: recruiting?.newApplicants7d
              ? t('summary.new_applicants', {
                  count: recruiting.newApplicants7d,
                })
              : t('summary.no_new_applicants'),
            icon: Briefcase,
            toneClass: styles.toneBlue,
            path: '/ats',
            series: seriesPoints(recruiting?.series, locale),
          }
        : null,
      // Assigned-asset count deliberately absent: assets already have their
      // own panel further down the dashboard.
    ];
    return list.filter((stat): stat is StatCardData => stat !== null);
  }, [cards, locale, t, tLeave, recruitingOn, openUpcoming]);

  /* Dropped rather than greyed out: a card whose page the viewer can't open has
     no state left worth showing. The skeleton counts the survivors so the grid
     doesn't reflow once data lands. */
  const visibleStats = useMemo(
    () => stats.filter((stat) => permissions.has(stat.scope)),
    [permissions, stats]
  );

  return (
    <>
      <div className={styles.grid} data-tour="dashboard-summary">
        {isLoading ? (
          <SummarySkeleton count={visibleStats.length} />
        ) : (
          visibleStats.map(({ key, ...stat }) => (
            <StatCard key={key} {...stat} locale={locale} />
          ))
        )}
      </div>
      {permissions.has(permissionKeys.employee.read) && (
        <UpcomingChangesDrawer
          open={upcomingOpen}
          onOpenChange={setUpcomingOpen}
        />
      )}
    </>
  );
};

export default memo(Summary);
